import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { EndpointConfig } from 'src/app/config/endpoint-config';
import { HttpHelper } from '../../utils/http-helper';
import { DialogService } from 'src/app/service/dialog-service';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { ImageRequests } from 'src/app/model/imageRequest/imageRequest';
import { mergeMap, tap, finalize, filter, map, switchMap } from 'rxjs/operators';
import { Observable, noop } from 'rxjs';
import { WebDataApiService } from 'src/app/service/server/web-data-api-service';
import {
  ActivatedRoute,
  Router,
} from '@angular/router';
import { RouterService } from '../../service/router-service';
import { ImageModifyModel } from 'src/app/component/image-modify-dialog/image-modify-dialog.component';
import { AuthenticatedImagePipe } from 'src/app/pipe/authenticated-image';

type SearchForm = {
  fromDate: Date | null;
  toDate: Date | null;
  status: string[] | null;
  reasonCode: string | null;
  requestedBy: string[] | null;
};

type searchFormConditions = {
  fromDate: string | null;
  toDate: string | null;
  status: string[] | null;
  reasonCode: string | null;
  requestedBy: string[] | null;
}

interface reasonOption {
  reasonCode: string;
  reasonText: string;
}

  //select box
  interface reasonResponse {
    reasons: reasonOption[];
    totalCount: number;
  }

@Component({
  selector: 'app-image-management',
  templateUrl: './image-management.component.html',
  styleUrls: ['./image-management.component.css'],
})

export class ImageManagementComponent implements OnInit {
  readonly allStatusValue = '__ALL__';
  @ViewChild('scrollHost') scrollHost!: ElementRef<HTMLElement>
  currentPage = 1;
  pageSize = 20;
  total = 0;

  rows: any[] = [];
  pages: (number | '…')[] = [];

  private documentIdSignedUrlMap: { [key: string]: string } = {}
  private INTERVAL_CHUNK_SIZE = 20
  imageReq: ImageRequests | null = null;

  constructor(
    private dialog: DialogService,
    private endpointConfig: EndpointConfig,
    private http: HttpClient,
    private httpHelper: HttpHelper,
    public webDataApiService: WebDataApiService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private routerService: RouterService
  ) {
  }
  //入力用
  formConditions: SearchForm = {
    fromDate: null,
    toDate: null,
    status: null,
    reasonCode: null,
    requestedBy: null,
  }

  //API用
  conditions: searchFormConditions = {
    fromDate: null,
    toDate: null,
    status: null,
    reasonCode: null,
    requestedBy: null
  }

  //page数
  get totalPages() {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  ngOnInit(): void {
    this.loadStatusOptions();
    const qp = this.activatedRoute.snapshot.queryParams;
    const qpMap = this.activatedRoute.snapshot.queryParamMap;

    //page
    const pageParam = qp['page'] !== undefined ? qp['page'] : '1';
    const page = Number(pageParam);
    this.currentPage = isNaN(page) ? 1 : page;

    const statuses = qpMap.getAll('status');
    const normalizedStatus = this.normalizeStatus(statuses.length > 0 ? statuses : qp['status']);

    //API用
    this.conditions = {
      fromDate: qp['fromDate'] || null,
      toDate: qp['toDate'] || null,
      status: normalizedStatus,
      reasonCode: qp['reasonCode'] || null,
      requestedBy: qp['requestedBy'] || null,
    };

    //フォームに戻す用
    this.formConditions = {
      fromDate: this.parseYmd(this.conditions.fromDate),
      toDate: this.parseYmd(this.conditions.toDate),
      status: this.conditions.status && this.conditions.status.length > 0 ? this.conditions.status : [this.allStatusValue],
      reasonCode: this.conditions.reasonCode,
      requestedBy: this.conditions.requestedBy,
    };

    this.loadPage(1);
  }

  reasons: reasonOption[] = [];
  selectedReasonCode: number | null = null;
  private loadStatusOptions(): void{
    var url = this.endpointConfig.riaApi.imageRequests.reasons.getUrl();
    this.http.get<reasonResponse>(url)
    .subscribe((res) => {
      this.reasons = res.reasons || [];
    });
  }

  private parseYmd(s: string | null): Date | null {
    if (!s || s.length !== 8) return null;
    const yyyy = Number(s.slice(0, 4));
    const MM = Number(s.slice(4, 6));
    const dd = Number(s.slice(6, 8));
    return new Date(yyyy, MM - 1, dd);
  }

  private normalizeStatus(status: string[] | string | null | undefined): string[] | null {
    if (Array.isArray(status)) {
      const expanded = status.reduce((result: string[], value: string) => {
        if (!value) {
          return result;
        }

        return result.concat(value.split(','));
      }, []);
      const normalized = expanded.filter((value: string): value is string => !!value && value !== 'null' && value !== this.allStatusValue);
      return normalized.length > 0 ? normalized : null;
    }

    if (!status || status === 'null' || status === this.allStatusValue) {
      return null;
    }

    return status.split(',').filter(value => !!value);
  }

  private serializeStatus(status: string[] | null): string | null {
    return status && status.length > 0 ? status.join(',') : null;
  }

  onStatusChange(status: string[] | null): void {
    const previousStatus = this.formConditions.status || [];

    if (!status || status.length === 0) {
      this.formConditions.status = [this.allStatusValue];
      return;
    }

    const hasAll = status.includes(this.allStatusValue);
    if (hasAll && status.length > 1) {
      const previousHadAll = previousStatus.includes(this.allStatusValue);
      this.formConditions.status = previousHadAll
        ? status.filter(value => value !== this.allStatusValue)
        : [this.allStatusValue];
      return;
    }

    this.formConditions.status = status;
  }

  onSearch() {
    const normalizedStatus = this.normalizeStatus(this.formConditions.status);
    const serializedStatus = this.serializeStatus(normalizedStatus);

    this.conditions = {
      fromDate: this.toYmd(this.formConditions.fromDate),
      toDate: this.toYmd(this.formConditions.toDate),
      status: normalizedStatus,
      reasonCode: this.formConditions.reasonCode,
      requestedBy: this.formConditions.requestedBy
    };

    this.currentPage = 1;

    //URLに検索条件を反映
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { ...this.conditions, status: serializedStatus, page: this.currentPage },
      queryParamsHandling: 'merge',
    });
    this.loadPage(1);
  }

  loadPage(page: number): void {
    this.dialog.loadingShow()
    page = Math.max(1, Math.min(page, this.totalPages || Number.MAX_SAFE_INTEGER));
    const offset = (page - 1) * this.pageSize;
    const c = this.conditions;

    const paramsObj: any = {
      limit: String(this.pageSize),
      offset: String(offset)
    };
    if (c.fromDate) paramsObj.fromDate = c.fromDate;
    if (c.toDate) paramsObj.toDate = c.toDate
    // status が null や空の場合（ALL選択時）はパラメータに含めない
    if (c.status && c.status.length > 0) paramsObj.status = this.serializeStatus(c.status)
    if (c.reasonCode) paramsObj.reasonCode = c.reasonCode
    if (c.requestedBy) paramsObj.requestedBy = c.requestedBy

    this.getImageReq(paramsObj).pipe(
      mergeMap(data => {
        return this.webDataApiService.createDocumentIdSignedUrlMap(data.requests.slice(0, this.INTERVAL_CHUNK_SIZE))
          .pipe(
            map((documentIdSignedUrlMap) => {
              this.documentIdSignedUrlMap = documentIdSignedUrlMap
              return data
            })
          )
      }),
      finalize(() => this.dialog.loadingHide())
    ).subscribe((res: ImageRequests) => {
      this.rows = res.requests;
      this.total = res.total;
      this.currentPage = page;
      this.pages = this.buildPages(this.currentPage, this.totalPages);

      //ページの一番上に
      this.scrollWindowTop();
    });
  }

  goToPage(p: number | '…') {
    if (p === '…') return;
    if (p === this.currentPage) return;

    this.currentPage = p as number;

    //URLのpageだけ更新
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { page: this.currentPage },
      queryParamsHandling: 'merge',
    });
    this.loadPage(this.currentPage);
  }

  prev() {
    if (this.currentPage > 1) this.loadPage(this.currentPage - 1);
  }
  next() {
    if (this.currentPage < this.totalPages) this.loadPage(this.currentPage + 1);
  }

  back() {
    if (this.routerService.getCurrentUrl() === this.routerService.getPreviousUrl()) {
      this.router.navigate(['tag/home'])
    } else {
      this.router.navigateByUrl(this.routerService.getPreviousUrl())
    }
  }

  private buildPages(current: number, total: number, win = 1): (number | '…')[] {
    const FULL_SHOW_THRESHOLD = 5
    if (total <= FULL_SHOW_THRESHOLD) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: (number | '…')[] = [];
    pages.push(1);

    ///現在ページから前後1ページを表示
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);

    // 2からstartの間が空いてたら…
    if (start > 2) {
      pages.push('…');
    }

    // 中央値
    for (let p = start; p <= end; p++) {
      pages.push(p);
    }

    // endからtotal -1は…
    if (end < total - 1) {
      pages.push('…');
    }

    // 最後のページは常に表示
    pages.push(total);

    return pages;
  }

  get rangeText(): string {
    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.total, this.currentPage * this.pageSize);
    return this.total ? `${start}-${end} / ${this.total}` : '0';
  }

  getImageSrc(src: string): string {
    let documentId = src.split('?')[0].split('/').slice(-1)[0]
    return this.documentIdSignedUrlMap[documentId]
  }

  getImageReq(paramsObj: { [key: string]: string }): Observable<ImageRequests> {
    var url = this.endpointConfig.riaApi.imageRequests.getUrl()
    const params = new HttpParams({ fromObject: paramsObj })
    return this.http.get<ImageRequests>(url, { params })
  }

  private toYmd(d: Date | null): string | null {
    if (!d) return null;
    const yyyy = d.getFullYear();
    const MM = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}${MM}${dd}`;
  }

  // ページ遷移後にTopに
  private scrollWindowTop() {
    const el = this.scrollHost.nativeElement;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  addImageModify(paramsObj: { [key: string]: string }): Observable<ImageModifyModel> {
    var url = this.endpointConfig.riaApi.imageRequests.putUrl()
    return this.http.put<ImageModifyModel>(url, paramsObj)
  }

  // Image Request Historyへの登録処理
  doImageModify(row:any) {
    const signedUrl = this.getImageSrc(row.src)
    const data: ImageModifyModel =
      {
        src: row.src,
        signedUrlSrc: signedUrl,
        reasonCode: row.reasonCode,
        comment: row.comment,
        status: row.status,
        requestDetail: row.requestDetail,
      };

    this.dialog.imageModify(data)
      .pipe(
        filter(function (x: any) { return x.ok; }),
        tap(() => this.dialog.loadingShow()),
        switchMap((x: any) => {
          //ダイアログからの返却値
          const imageModify = x.imageModify;
          //既存のRequestDetailとモーダルのRotationをMerge
          const base = row && row.requestDetail ? row.requestDetail : {};
          const mergedRequestDetail = Object.assign({}, base, { rotation: imageModify.requestDetail.rotation});
          //APIのBody作成
          const paramsObj: any = {
            src: imageModify.src,
            reasonCode: imageModify.reasonCode,
            comment: imageModify.comment,
            status: imageModify.status,
            requestDetail: mergedRequestDetail
          };
          //APIの呼び出し
          return this.addImageModify(paramsObj);
        }),
        finalize(() => this.dialog.loadingHide())
      ).subscribe(
        (res: any) => {
          this.dialog.info('Update', 'Image Modification has been completed');
          // パイプのキャッシュをクリアして画像の更新を反映させる
          AuthenticatedImagePipe.clearCache();
          //再読み込み
          this.loadPage(this.currentPage);
        },
        (res: HttpErrorResponse) => {
          this.dialog.loadingHide()
          if (res.status == 400 || res.status == 409) {
            var messages = <string[]>res.error.errors.map((e: any) => e.message)

            this.dialog.error('Error', messages.join('<br>'))
              .subscribe(() => noop())
          } else {
            this.httpHelper.getDefaultErrorHandler()(res)
          }
        }
      );
  }
}
